import { Locale } from "../locale";
const R = require("ramda");

interface Money {
  amount: number;
  currencyCode: string;
}

// Keep in Sync with API's Currency List
// Check multiplier in: https://en.wikipedia.org/wiki/ISO_4217#Active_codes

interface Multipliers {
  [code: string]: number;
}

const multipliers: Multipliers = {
  AUD: 100,
  BRL: 100,
  CAD: 100,
  CNY: 100,
  EUR: 100,
  GBP: 100,
  HKD: 100,
  ILS: 100,
  INR: 100,
  JPY: 1,
  KRW: 1,
  MYR: 100,
  NZD: 100,
  PHP: 100,
  SGD: 100,
  SEK: 100,
  THB: 100,
  TWD: 100,
  USD: 100,
};

interface CurrencyCodes {
  [code: string]: string;
}

export const currencyCodes: CurrencyCodes = {
  AUD: "AUD",
  BRL: "BRL",
  CAD: "CAD",
  CNY: "CNY",
  EUR: "EUR",
  GBP: "GBP",
  HKD: "HKD",
  ILS: "ILS",
  INR: "INR",
  JPY: "JPY",
  KRW: "KRW",
  MYR: "MYR",
  NZD: "NZD",
  PHP: "PHP",
  SEK: "SEK",
  SGD: "SGD",
  THB: "THB",
  TWD: "TWD",
  USD: "USD",
};

export function parseCurrencyCode(codeString: string) {
  if (!Object.keys(currencyCodes).includes(codeString)) {
    throw new Error("Invalid code.");
  }

  return codeString;
}

interface NumberFormatter {
  [code: string]: Intl.NumberFormat;
}

const NumberFormats: NumberFormatter = {};

function getNumberFormat(currencyCode: string) {
  if (!NumberFormats[currencyCode]) {
    NumberFormats[currencyCode] = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      style: "currency",
      currencyDisplay: "symbol",
      currency: currencyCode,
    });
  }

  return NumberFormats[currencyCode];
}

const formats = {
  [currencyCodes.USD]: (formatted: string) => formatted.replace(/^\$/, "US$"),
  [currencyCodes.SGD]: (formatted: string) => formatted.replace(/^SGD\s/, "S$"),
  [currencyCodes.HKD]: (formatted: string) => formatted.replace(/^$/, "HK$"),
  [currencyCodes.CAD]: (formatted: string) => formatted.replace(/^CA\$/, "C$"),
  [currencyCodes.MYR]: (formatted: string) => formatted.replace(/^MYR\s/, "RM"),
  [currencyCodes.CNY]: (formatted: string) => formatted.replace(/^CN¥/, "¥"),
  [currencyCodes.THB]: (formatted: string) => formatted.replace(/^THB\s/, "฿"),
};

export function format({ amount, currencyCode }: Money): string {
  const formatter = getNumberFormat(currencyCode);
  const formatted = formatter.format(amount);

  const fn = formats[currencyCode] || ((formatted) => formatted);

  return fn(formatted);
}

export function amountToPaymentUnits({ amount, currencyCode }: Money): number {
  const multiplier = multipliers[currencyCode];
  if (multiplier) {
    return amount * multiplier;
  }

  return amount;
}

function minorToMajorUnits({ amount, currencyCode }: Money): number {
  const multiplier = multipliers[currencyCode];

  if (multiplier) {
    return amount / multiplier;
  }

  return amount;
}

export function calculateDiscountPercentage(amount: number, newAmount: number) {
  return 100 - (newAmount * 100) / amount;
}

export function calculateDiscountPrice(
  money: Money,
  discountPercentage: number
) {
  const amountInMinorUnits = amountToPaymentUnits(money);
  const discountPriceInMinorUnits =
    amountInMinorUnits * ((100 - discountPercentage) / 100);

  return minorToMajorUnits({
    amount: discountPriceInMinorUnits,
    currencyCode: money.currencyCode,
  });
}

export const defaultCurrencyCode = currencyCodes.USD;

function match<T, U>(value: T, cases: Array<[T, U]>, defaultMatch: U) {
  const result = cases.find((kase) => {
    const pattern = kase;
    const patternKeys = Object.keys(pattern);
    const valueWithPatternKeys = R.pickAll(patternKeys, value);

    return R.equals(pattern, valueWithPatternKeys);
  });

  return result ? result[1] : defaultMatch;
}

interface LocaleMatcher {
  language?: string;
  script?: string;
  country?: string;
  countryIsoCode?: string;
}

export function mapLanguageToProbableCurrency(
  locale?: Locale,
  countryIsoCode?: string
) {
  const matcher: LocaleMatcher = { ...locale, countryIsoCode };

  try {
    return match(
      matcher,
      [
        [{ language: "cn" }, currencyCodes.CNY],
        [{ language: "de", country: "DE" }, currencyCodes.EUR],
        [{ language: "de", country: "AT" }, currencyCodes.EUR],
        [{ language: "el" }, currencyCodes.EUR],
        [{ language: "en", countryIsoCode: "AU" }, currencyCodes.AUD],
        [{ language: "en", country: "GB" }, currencyCodes.GBP],
        [{ language: "en", country: "AU" }, currencyCodes.AUD],
        [{ language: "en", country: "CA" }, currencyCodes.CAD],
        [{ language: "en", country: "IE" }, currencyCodes.EUR],
        [{ language: "en", country: "NZ" }, currencyCodes.NZD],
        [{ language: "en", country: "PH" }, currencyCodes.PHP],
        [{ language: "en", country: "SG" }, currencyCodes.SGD],
        [{ language: "es", country: "ES" }, currencyCodes.EUR],
        [{ language: "fi", country: "FI" }, currencyCodes.EUR],
        [{ language: "fr", country: "CA" }, currencyCodes.CAD],
        [{ language: "fr" }, currencyCodes.EUR],
        [{ language: "he", country: "IL" }, currencyCodes.ILS],
        [{ language: "it", country: "IT" }, currencyCodes.EUR],
        [{ language: "ja" }, currencyCodes.JPY],
        [{ language: "ko" }, currencyCodes.KRW],
        [{ language: "ms" }, currencyCodes.MYR],
        [{ language: "nl", country: "BE" }, currencyCodes.EUR],
        [{ language: "nl", country: "NL" }, currencyCodes.EUR],
        [{ language: "pl" }, currencyCodes.EUR],
        [{ language: "pt", country: "BR" }, currencyCodes.BRL],
        [{ language: "pt", country: "PT" }, currencyCodes.EUR],
        [{ language: "se", country: "FI" }, currencyCodes.EUR],
        [{ language: "sv", country: "FI" }, currencyCodes.EUR],
        [{ language: "sv", country: "SE" }, currencyCodes.SEK],
        [{ language: "th" }, currencyCodes.THB],
        [{ language: "tl", country: "PH" }, currencyCodes.PHP],
        [{ language: "zh" }, currencyCodes.TWD],
      ],
      defaultCurrencyCode
    );
  } catch (err) {
    return defaultCurrencyCode;
  }
}
